<template lang="pug">
  #lessons_page.container.page--lessons.is-relative(
    v-shortkey="['arrowright']"
    @shortkey="nextPage()"
  )
    .box.has-shadow(
      v-shortkey="['arrowleft']"
      @shortkey="previousPage()"
    )
      .columns
        .column
          h3.title.has-text-left
            | Lessons

      // EXPLANATIONS
      empty-state(
        v-if="shouldShowEmptyMessage"
        icon="share"
        title="No shared lessons yet"
        message="Once you share your first lesson, your students will appear on this page.")

      b-table.w-100.with-search(
        v-if="allLessons.length > 0 || loading || hasActiveFilters"
        checkable
        :checked-rows.sync="checkedRows"
        narrowed
        hoverable
        sort-icon="arrow-drop-up"
        default-sort="updated_at"
        default-sort-direction="desc"
        :loading="loading"
        :data="lessons"
        @mouseenter="() => {}"
        backend-filtering
        @filters-change="onFiltersChange"
      )
        //- Title
        b-table-column(
          v-slot="props"
          field="title"
          label="Title"
          width="320"
          sortable
          searchable
          align="left"
        )
          router-link.is-smooth.has-text-grey-dark.has-text-weight-bold.is-block.is-lesson-link(
            :to="{name: 'LiveLesson', params:{ id: props.row.uuid } }")
            i.flag.mr-2(:class="'flag-'+props.row.language_to")
            | {{ props.row.title }}

        //- Sentences
        b-table-column(v-slot="props" field="sentences_nb" label="Sentences" sortable numeric)
          span.has-text-grey
            | {{ props.row.sentences_nb }} sentence{{ (props.row.sentences_nb > 1) ? 's' : '' }}

        //- Owner
        b-table-column(v-slot="props" field="user_id" label="Owner" sortable)
          span(v-if="user.id ==  props.row.user_id")
            | Me
          .has-size-30px(v-else-if="usersById[props.row.user_id]")
            user-picture-ui(:user="usersById[props.row.user_id]")
          //-user-picture-ui(v-if="user.id ==  props.row.user_id" :user="user")

        //- Visitors
        b-table-column(v-slot="props" field="visitors" label="Shared with" sortable)
          router-link(
            v-for="visitor in props.row.visitors" :key="visitor.id"
            :to="`/students/${visitor.id}`"
            )
            span.has-size-30px(v-if="usersById[visitor.id]")
              user-picture-ui.mr-1.is-inline-block(:user="usersById[visitor.id]")
            span.has-size-30px(v-else)
              user-picture-ui.mr-1.is-inline-block(:user="visitor")

        //- Updated at
        b-table-column(v-slot="props" field="updated_at" label="Updated at" sortable width="130")
          | {{ formatDate(props.row.updated_at) }}

        //- Lesson actions
        template(
          #footer
          v-if="checkedRows.length"
          )
          .is-flex.mt-2
            button.button.is-danger.is-outlined.ml-auto.mr-2(
              @click="removeLessons"
              v-confirm="'Are you sure you want to <b>delete the lessons</b>?'"
            ) Delete
            button.button.is-primary(@click="exportToAnkiModal = !exportToAnkiModal") Export to Anki

      // PAGINATION
      .mt-4(v-if="totalPages > 1")
        b-pagination(
          :total="totalLessons"
          :current.sync="currentPage"
          :per-page="perPage"
          @change="onPageChange"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
        )
          template(#previous="{ page }")
            b-icon(icon="chevron-left")
          template(#next="{ page }")
            b-icon(icon="chevron-right")

      // No results found for search/filter
      article.message.is-light.mt-4(v-if="allLessons.length === 0 && !loading && hasActiveFilters")
        .message-body
          | No lessons found matching your search or filter criteria. Try a different search term or language.

    b-modal#export_to_anki_modal(:active.sync="exportToAnkiModal" :width="400")
      batch-anki-modal-body(
        :lessonUuids="checkedRows.map(row => row.uuid)"
        @close="exportToAnkiModal = false"
      )
</template>


<script>
  import {mapGetters, mapActions} from "vuex";
  import UserPictureUi from "../UserPictureUI";
  import debounce from "lodash/debounce";
  import BatchAnkiModalBody from "../BatchAnkiModalBody";
  import EmptyState from "../ui/EmptyState";

  export default {
    name:       "LessonsPage",
    components: {BatchAnkiModalBody, UserPictureUi, EmptyState},
    data() {
      return {
        loading: false,
        exportToAnkiModal: false,
        checkedRows: [],
        currentPage: 1,
        perPage: 20,
        searchTerm: ''
      };
    },
    computed:   {
      ...mapGetters(["allLessons", "user", "usersById", "lessonsPagination"]),
      lessons() {
        return this.allLessons
      },
      totalPages() {
        return this.lessonsPagination?.totalPages || 1
      },
      totalLessons() {
        return this.lessonsPagination?.total || 0
      },
      selectedLanguage() {
        return this.$route.params.lang || null
      },
      hasActiveFilters() {
        return !!(this.searchTerm || this.selectedLanguage)
      },
      shouldShowEmptyMessage() {
        return this.allLessons.length === 0 && !this.loading && !this.hasActiveFilters
      }
    },
    watch: {
      '$route.params.lang'() {
        // Reset to first page when language changes
        this.currentPage = 1
        this.fetchLessons()
      }
    },
    created() {
      // created_at: "2020-05-30T04:43:14.463Z"
      // first_name: null
      // id: "58"
      // picture: null
      // title: "Je teste"
      // updated_at: "2020-05-30T04:43:14.463Z"
      // user_id: "1"
      // uuid: "01cf8..."
      this.$store.dispatch('queryUsers')
      this.fetchLessons()
    },
    mounted() {
      this.$nextTick(() => {
        this.$qs('#lessons_page table input[type=text]').focus()
      })
      // setTimeout(() => {
      // }, 200)
      // setTimeout(() => {
      //   this.$qs('#lessons_page table input').focus()
      // }, 200)
    },
    methods: {
      fetchLessons() {
        this.loading = true
        const params = {
          page: this.currentPage,
          limit: this.perPage
        }

        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        if (this.selectedLanguage) {
          params.language_to = this.selectedLanguage
        }

        this.$store.dispatch('allLessons', params).finally(() => {
          this.loading = false
        })
      },

      onPageChange(page) {
        this.currentPage = page
        this.fetchLessons()
      },

      onFiltersChange: debounce(function(filters) {
        // Extract search term from title filter
        this.searchTerm = filters.title || ''
        // Reset to first page when searching
        this.currentPage = 1
        this.fetchLessons()
      }, 500),

      previousPage() {
        if (this.currentPage > 1) {
          this.currentPage--
          this.fetchLessons()
        }
      },

      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++
          this.fetchLessons()
        }
      },

      preload: debounce(function(lesson) {
        this.$store.dispatch("findLesson", { id: lesson.uuid })
      }, 200),

      removeLessons() {
        this.$store.dispatch('batchDestroyLessons', this.checkedRows.map(row => row.uuid)).then(() => {
          this.$toast.success("Lessons deleted successfully.");
          this.checkedRows = [];
          this.fetchLessons()
        })
      },

      formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      }
    }
  };
</script>


<style lang="scss">
  @import 'colors';

  .page--lessons {
    tr:nth-child(2) {
      th { height: 0px; padding: 0; border-width: 1px}
    }

    table {
      .is-lesson-link {
        text-align: left;
        &:hover {
          color: $primary !important;
        }
      }
    }

    [data-label="Title"] {
      td { padding: 0; }
      a { padding: 0.25em 0.5em ;
      }
    }
    i.flag {
      transform: scale(0.8);
    }
    .avatar {
      display: inline-block;
      vertical-align: middle;
    }
  }
</style>
